/**
 * useRiskRemediatorData — REST variant of the S3 hooks.
 *
 * Loads the 5 Risk Remediator REST payloads in parallel (risks, managers,
 * directs, ASR, VPC). Mirrors UseS3DatasetResult shape so consumers can be
 * swapped for the S3 hooks in Phase 5.
 *
 * No cache on v1 — REST calls are cheap + parameterised by manager/duration,
 * which we expose as inputs. Defaults come from RISK_REMEDIATOR_INITIAL_DATA.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from 'src/logger';
import { RISK_REMEDIATOR_INITIAL_DATA } from '../../risk-remediator/constants';
import {
  fetchDirects,
  fetchManagers,
  fetchRiskRemediatorAsrData,
  fetchRiskRemediatorRisks,
  fetchRiskRemediatorVpcData
} from '../../risk-remediator/utils';
import { RemediatorManagerList, RemediatorPayload, RiskRemediatorData } from '../../risk-remediator/RiskRemediatorTypes';

export interface RiskRemediatorBundle {
  risks: RiskRemediatorData;
  managers: RemediatorManagerList;
  directs: RemediatorPayload;
  asr: RemediatorPayload;
  vpc: RemediatorPayload;
}

export interface UseRiskRemediatorDataResult {
  data: RiskRemediatorBundle | undefined;
  loading: boolean;
  error: Error | null;
  /** No last-updated source for REST — always null. Preserved for shape parity. */
  lastUpdatedAt: string | null;
  refetch: () => void;
}

export interface UseRiskRemediatorDataOptions {
  duration?: string;
  manager?: string;
}

const EMPTY_PAYLOAD: RemediatorPayload = { details: '' } as RemediatorPayload;

export function useRiskRemediatorData(opts: UseRiskRemediatorDataOptions = {}): UseRiskRemediatorDataResult {
  const duration = opts.duration ?? RISK_REMEDIATOR_INITIAL_DATA.duration;
  const manager = opts.manager ?? RISK_REMEDIATOR_INITIAL_DATA.manager;

  const [data, setData] = useState<RiskRemediatorBundle | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchRiskRemediatorRisks(duration, manager),
      fetchManagers(),
      fetchDirects(manager),
      fetchRiskRemediatorAsrData(manager),
      fetchRiskRemediatorVpcData(manager)
    ])
      .then(([risks, managers, directs, asr, vpc]) => {
        if (cancelledRef.current) return;
        setData({
          risks: (risks ?? RISK_REMEDIATOR_INITIAL_DATA) as RiskRemediatorData,
          managers: (managers ?? []) as RemediatorManagerList,
          directs: (directs ?? EMPTY_PAYLOAD) as RemediatorPayload,
          asr: (asr ?? EMPTY_PAYLOAD) as RemediatorPayload,
          vpc: (vpc ?? EMPTY_PAYLOAD) as RemediatorPayload
        });
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelledRef.current) return;
        const e = err instanceof Error ? err : new Error(String(err));
        logger.error('useRiskRemediatorData fetch failed', e);
        setError(e);
        setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [duration, manager, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { data, loading, error, lastUpdatedAt: null, refetch };
}
